import { CasePlan } from '@app/types/payment/subscription';

/**
 * Plans are sold as a product at a tier, named "<Product> <Tier>" by
 * seed_product_plans, for example "Med Intel Starter".
 *
 * The catalogue used to be a flat list of three case plans, so the UI keyed a
 * tier off active_case_limit. Per-product plans meter by product and leave that
 * limit at 0, which made every tier look identical and collapsed them onto
 * whichever plan happened to sort first. Identity is the plan id now, and the
 * product and tier are read back off the name.
 */
export const TIER_ORDER = ['Starter', 'Growth', 'Pro'] as const;

export type Tier = (typeof TIER_ORDER)[number];

export interface SplitPlanName {
  product: string;
  tier: Tier | '';
}

export function splitPlanName(name: string): SplitPlanName {
  const tier = TIER_ORDER.find((t) => name.endsWith(` ${t}`));
  if (!tier) return { product: name, tier: '' };
  return { product: name.slice(0, name.length - tier.length - 1), tier };
}

export function productOf(plan: CasePlan): string {
  return splitPlanName(plan.name).product;
}

export function tierOf(plan: CasePlan): Tier | '' {
  return splitPlanName(plan.name).tier;
}

/** Distinct products, in the order the API returned them (cheapest first). */
export function productsOf(plans: CasePlan[]): string[] {
  const seen: string[] = [];
  plans.forEach((p) => {
    const product = productOf(p);
    if (!seen.includes(product)) seen.push(product);
  });
  return seen;
}

/** The plans for one product, ordered Starter, Growth, Pro. */
export function plansForProduct(
  plans: CasePlan[],
  product: string
): CasePlan[] {
  return plans
    .filter((p) => productOf(p) === product)
    .sort(
      (a, b) =>
        TIER_ORDER.indexOf(tierOf(a) as Tier) -
        TIER_ORDER.indexOf(tierOf(b) as Tier)
    );
}

/**
 * What a plan includes per month, as display strings.
 *
 * Prefers the feature list the backend ships, so the wording that appears in
 * the app is the wording the plan was created with rather than a second copy
 * that can drift from it. product_allowances is the fallback.
 */
export function planInclusions(plan: CasePlan): string[] {
  if (plan.features && plan.features.length > 0) return plan.features;
  return (plan.product_allowances ?? []).map((a) => {
    return `${a.quota} ${a.group} ${allowanceNoun(a.group, a.unit)} per month`;
  });
}

/**
 * The unit a monthly allowance is counted in, in the words the plan is sold
 * with. Deposition Insights is sold as "N depositions per month", and
 * deposition summaries and Key Takeaways reports both count as one
 * deposition (ZLAYT-1597).
 */
export function allowanceNoun(group: string, unit: string): string {
  if (unit === 'case') return 'cases';
  if (group === 'Deposition Insights') return 'depositions';
  return 'documents';
}

/** True when a plan has no monthly cap on any of its products. */
export function isUnlimited(plan: CasePlan): boolean {
  return (plan.product_allowances ?? []).length === 0;
}

export function annualSavingPercent(
  monthly: number | string | undefined,
  annual: number | string | undefined
): number | null {
  const m = Number(monthly);
  const a = Number(annual);
  if (!m || !a) return null;
  const full = m * 12;
  if (a >= full) return null;
  return Math.round(((full - a) / full) * 1000) / 10;
}
